/**
 * Animal Health — Eligible Cohort Workspace (v25.2 · Aug 2026)
 * ============================================================
 * The single operational workspace for AI evaluation. The page evolves:
 *   • Before AI: table of eligible assets with "Run AI" per row + bulk
 *     "Run AI on Cohort" button. AI Score column shows "—".
 *   • During AI: rows in-flight show "Running…" status. Live progress bar.
 *   • After AI: three tier cards + persistent tabs appear above the table.
 *     Each scored row shows the score + tier chip + "Deep Dive" action.
 * The Deep Dive modal opens inline — user never leaves this page.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Loader2, ArrowLeft, ArrowUpDown, ArrowUp, ArrowDown, Search,
  Sparkles, ScanEye, Download, PlayCircle, RotateCcw, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  TIER_META, TierChip, ScoreBar, AIStatusBadge, ConfidencePill, DeepDiveModal, AnimalDataPill,
} from './animalHealthShared';
import { InfoButton, HELP } from './animalHealthHelp';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api/animal-health';

export default function AnimalHealthCohortPage() {
  const { user } = useAuth();
  // Feb 2026 · Client-portal (Eligible Cohort read-only + clickable decisions).
  // When the signed-in user is a client scoped to portal_type='animal-health'
  // we suppress admin-only writes (Recompute, Run AI on Cohort, Reset, XLSX
  // export) so the middleware never fires a 403 toast for them.
  const isClientPortal = user?.role === 'client' && user?.portal_type === 'animal-health';
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [assessments, setAssessments] = useState({});   // {asset_id: assessment}
  const [animalStatus, setAnimalStatus] = useState({}); // {asset_id: 'available'|'manual_added'|...}
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tier, setTier] = useState('all');   // all|pursue|advance|pass|running|failed|unevaluated
  const [sort, setSort] = useState({ field: 'ai_opportunity_score', dir: 'desc' });
  const [selected, setSelected] = useState(new Set());
  const [running, setRunning] = useState(false);
  const [evalTask, setEvalTask] = useState(null);
  const [inflight, setInflight] = useState(new Set());   // asset_ids currently being scored
  const [failed, setFailed] = useState(new Set());        // asset_ids where a manual re-run errored
  const [ddItem, setDdItem] = useState(null);
  // v27 — cohort diff vs previous snapshot
  const [cohortDiff, setCohortDiff] = useState(null);
  const [recomputing, setRecomputing] = useState(false);

  const [loadError, setLoadError] = useState('');

  const loadCohort = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const wd = await axios.get(`${API}/screening-definition?workspace_id=default`, { withCredentials: true });
      // Feb 2026 · Cohort page load is split across four calls. On large
      // production cohorts (~1650+ scored assets) the /assessment/results
      // response was 20–80 MB (each doc carries deep_dive + evidence
      // blobs) — Cloudflare killed it at the 100 s / MB envelope and the
      // whole load failed with "Failed to load cohort". Fix: pass
      // ?compact=true so the results endpoint projects only the fields
      // the table renders (score / tier / confidence / manual decision).
      // Full deep-dive JSON is lazy-loaded per asset in openDeepDive()
      // when the user clicks the "Deep Dive" button.
      const [preview, results, cfg, diff] = await Promise.all([
        axios.post(
          `${API}/screening-definition/cohort-preview`,
          { definition: wd.data, eligible_limit: 20000, dropped_sample: 0 },
          { withCredentials: true, timeout: 120000 },
        ),
        axios.get(`${API}/assessment/results?limit=50000&compact=true`, { withCredentials: true, timeout: 60000 }),
        axios.get(`${API}/assessment/config`, { withCredentials: true }),
        axios.post(
          `${API}/screening-definition/recompute-diff`,
          { definition: wd.data, workspace_id: 'default' },
          { withCredentials: true, timeout: 60000 },
        ),
      ]);
      setAssets(preview.data?.eligible_assets || []);
      setTotal(preview.data?.eligible || 0);
      const map = {};
      (results.data?.items || []).forEach(a => { map[a.asset_id] = a; });
      setAssessments(map);
      setCriteria(cfg.data?.criteria || []);
      setCohortDiff(diff.data || null);
      // Feb 2026 · Fetch Animal Data status pills for every visible
      // asset in one shot so the new column renders without N requests.
      // Non-fatal if this call fails — the column just falls back to
      // "Not reviewed" pills.
      try {
        const ids = (preview.data?.eligible_assets || []).map(a => a.id).filter(Boolean);
        if (ids.length) {
          const s = await axios.post(`${API}/animal-data/status-bulk`, { asset_ids: ids }, { withCredentials: true, timeout: 30000 });
          setAnimalStatus(s.data?.status_map || {});
        }
      } catch { /* ignore — pill column falls back to "not_reviewed" */ }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to load cohort';
      setLoadError(String(msg));
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  const reloadResults = async () => {
    try {
      const r = await axios.get(`${API}/assessment/results?limit=50000&compact=true`, { withCredentials: true, timeout: 60000 });
      const map = {};
      (r.data?.items || []).forEach(a => { map[a.asset_id] = a; });
      setAssessments(map);
    } catch { /* ignore */ }
  };
  // Feb 2026 · Deep Dive lazily fetches the FULL assessment doc for one
  // asset (deep_dive JSON, evidence, rationale). Cohort list is fetched
  // in compact mode to keep the initial payload small; the extra call
  // here is cheap and only fires when the user opens the modal.
  const openDeepDive = async (compactAssessment) => {
    if (!compactAssessment?.asset_id) return;
    // Show the modal immediately with what we have so it feels snappy,
    // then swap in the full doc when it arrives.
    setDdItem(compactAssessment);
    try {
      const r = await axios.get(`${API}/assessment/asset/${compactAssessment.asset_id}`, { withCredentials: true, timeout: 30000 });
      if (r.data) setDdItem(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load deep dive');
    }
  };
  useEffect(() => { loadCohort(); }, []);

  // Poll running batch task; refresh assessments periodically while active.
  useEffect(() => {
    if (!evalTask?.task_id || evalTask.status === 'done' || evalTask.status === 'error') return;
    const iv = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/assessment/task/${evalTask.task_id}`, { withCredentials: true });
        setEvalTask(res.data);
        // Refresh in-progress assessments so completed rows update.
        await reloadResults();
        if (res.data?.status === 'done') {
          toast.success(`AI evaluation complete — ${res.data.assessed} assets scored`);
          setInflight(new Set());
        } else if (res.data?.status === 'error') {
          toast.error(`AI evaluation failed: ${res.data.error || 'unknown'}`);
          setInflight(new Set());
        }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [evalTask?.task_id, evalTask?.status]);

  // Derived row-level data
  const enriched = useMemo(() => assets.map(a => {
    const asmt = assessments[a.id];
    const isInflight = inflight.has(a.id);
    const isFailed = failed.has(a.id);
    let status = 'pending';
    if (asmt) status = 'completed';
    else if (isInflight) status = 'running';
    else if (isFailed) status = 'failed';
    return {
      ...a,
      assessment: asmt,
      ai_opportunity_score: asmt?.ai_opportunity_score ?? null,
      ai_confidence: asmt?.ai_confidence ?? null,
      tier_id: asmt?.recommendation?.id ?? null,
      recommendation: asmt?.recommendation ?? null,
      manual_decision: asmt?.manual_decision ?? null,
      ai_status: status,
    };
  }), [assets, assessments, inflight, failed]);

  const counts = useMemo(() => {
    const c = { total: enriched.length, pursue: 0, advance: 0, pass: 0,
                evaluated: 0, pending: 0, running: 0, failed: 0 };
    enriched.forEach(a => {
      if (a.assessment) {
        c.evaluated += 1;
        const id = a.tier_id || 'pass';
        if (id in c) c[id] += 1;
      } else if (a.ai_status === 'running') {
        c.running += 1;
        c.pending += 1;
      } else if (a.ai_status === 'failed') {
        c.failed += 1;
        c.pending += 1;
      } else {
        c.pending += 1;
      }
    });
    return c;
  }, [enriched]);

  // v27 — count of newly-added assets (per last diff) that still need AI.
  const newPending = useMemo(() => {
    const s = new Set(cohortDiff?.newly_added || []);
    if (!s.size) return 0;
    return enriched.filter(a => s.has(a.id) && !a.assessment).length;
  }, [cohortDiff, enriched]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched.filter(a => {
      if (tier === 'unevaluated' && a.assessment) return false;
      if (tier === 'running' && a.ai_status !== 'running') return false;
      if (tier === 'failed' && a.ai_status !== 'failed') return false;
      if (tier === 'pursue' && a.tier_id !== 'pursue') return false;
      if (tier === 'advance' && a.tier_id !== 'advance') return false;
      if (tier === 'pass' && a.tier_id !== 'pass') return false;
      if (!q) return true;
      return [a.asset_name, a.company_name, a.therapeutic_area, a.primary_indication]
        .some(v => (v || '').toLowerCase().includes(q));
    });
  }, [enriched, tier, search]);

  // AI Confidence sort helper: High=3, Medium=2, Low=1, null=0.
  const _CONF_RANK = { High: 3, Medium: 2, Low: 1 };
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av = a[sort.field]; let bv = b[sort.field];
      if (sort.field === 'ai_confidence') {
        av = _CONF_RANK[av] || 0; bv = _CONF_RANK[bv] || 0;
      }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return sort.dir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av).toLowerCase(); const bs = String(bv).toLowerCase();
      return sort.dir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return arr;
  }, [filtered, sort]);

  // ── Virtual scrolling — replaces pagination (v27) ─────────────
  const scrollRef = useRef(null);
  const rowVirtualizer = useVirtualizer({
    count: sorted.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 44,
    overscan: 12,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();

  const toggleOne = (id) => setSelected(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAllVisible = () => {
    const eligibleIds = sorted.filter(a => !a.assessment).map(a => a.id);
    const allSelected = eligibleIds.length > 0 && eligibleIds.every(id => selected.has(id));
    setSelected(s => {
      const n = new Set(s);
      eligibleIds.forEach(id => { if (allSelected) n.delete(id); else n.add(id); });
      return n;
    });
  };
  const clearSel = () => setSelected(new Set());

  const runCohortEval = async (scope) => {
    let limit = 100;
    let inflightIds = [];
    if (scope === 'selected') {
      inflightIds = Array.from(selected).filter(id => !assessments[id]);
      limit = Math.min(5000, inflightIds.length);
    } else if (scope === 'all') {
      inflightIds = enriched.filter(a => !a.assessment).map(a => a.id);
      limit = Math.min(5000, inflightIds.length);
    } else if (scope === 'new') {
      // v27 — only newly-added assets (per the last recompute diff) that are not yet scored
      const newSet = new Set((cohortDiff?.newly_added) || []);
      inflightIds = enriched
        .filter(a => newSet.has(a.id) && !a.assessment)
        .map(a => a.id);
      limit = Math.min(5000, inflightIds.length);
    }
    if (!limit) { toast.error('Nothing to evaluate (all selected assets are already scored)'); return; }
    setRunning(true);
    setInflight(new Set(inflightIds));
    // Clear failed state for anything we're about to retry.
    setFailed(f => { const n = new Set(f); inflightIds.forEach(id => n.delete(id)); return n; });
    try {
      const res = await axios.post(
        `${API}/assessment/cohort`,
        { limit, only_missing: true, asset_ids: scope === 'new' ? inflightIds : undefined },
        { withCredentials: true, timeout: 30000 },
      );
      setEvalTask({ ...res.data, scanned: 0, assessed: 0, total: 0 });
      toast.success(`AI evaluation started on ${limit} asset${limit === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start AI evaluation');
      setInflight(new Set());
    } finally {
      setRunning(false);
    }
  };

  // v27 — Manual Recompute: re-runs the eligibility scan against the
  // saved rules and refreshes the diff summary. Used when the user has
  // just edited screening rules on the other page and wants to see the
  // Added / Removed counts before running AI.
  const recomputeCohort = async () => {
    setRecomputing(true);
    try {
      await loadCohort();
      toast.success('Cohort recomputed');
    } finally {
      setRecomputing(false);
    }
  };

  const runSingle = async (assetId) => {
    setInflight(s => new Set(s).add(assetId));
    setFailed(f => { const n = new Set(f); n.delete(assetId); return n; });
    try {
      const res = await axios.post(
        `${API}/assessment/asset/${assetId}`, {},
        { withCredentials: true, timeout: 90000 },
      );
      setAssessments(m => ({ ...m, [assetId]: res.data }));
      toast.success(`Scored · ${res.data?.ai_opportunity_score}/100 · ${res.data?.recommendation?.label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI evaluation failed');
      setFailed(f => new Set(f).add(assetId));
    } finally {
      setInflight(s => { const n = new Set(s); n.delete(assetId); return n; });
    }
  };

  const exportCsv = () => {
    const rows = [['Asset', 'Company', 'Phase', 'Therapeutic Area', 'Indication', 'Target', 'Modality', 'AI Score', 'AI Confidence', 'Decision Category', 'Manual Decision']];
    sorted.forEach(a => rows.push([
      a.asset_name || '', a.company_name || '', a.phase || '',
      a.therapeutic_area || '',
      a.primary_indication || a.indication || '',
      a.target || '',
      a.modality || '',
      a.ai_opportunity_score ?? '', a.ai_confidence || '',
      TIER_META[a.tier_id]?.label || '', a.manual_decision?.label || '',
    ]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const el = document.createElement('a');
    el.href = url; el.download = 'Modulus-AH-Eligible-Cohort.csv';
    document.body.appendChild(el); el.click(); el.remove();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  // Helper — format ETA seconds as "12 min" / "45 sec".
  const formatEta = (s) => {
    if (s == null || !Number.isFinite(s)) return '—';
    if (s < 60) return `${Math.max(1, Math.round(s))} sec`;
    if (s < 3600) return `${Math.round(s / 60)} min`;
    return `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m`;
  };

  const onManualDecisionSaved = (assetId, manualDecision) => {
    setAssessments(m => ({
      ...m,
      [assetId]: m[assetId] ? { ...m[assetId], manual_decision: manualDecision } : m[assetId],
    }));
    setDdItem(d => (d && d.asset_id === assetId ? { ...d, manual_decision: manualDecision } : d));
  };

  const pctOf = (n) => counts.evaluated ? Math.round(100 * n / counts.evaluated) : 0;
  const hasAny = counts.evaluated > 0;

  return (
    <div className="p-6 overflow-y-auto min-h-screen bg-slate-50" data-testid="ah-cohort-page">
      <div className="max-w-[1400px] mx-auto">
        <header className="mb-4 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <PlayCircle className="w-5 h-5 text-emerald-600" />
              <h1 className="text-2xl font-semibold text-slate-900">Eligible Cohort Workspace</h1>
              <InfoButton {...HELP.workspace} testId="info-workspace" />
            </div>
            <p className="text-[13px] text-slate-600 flex items-center gap-1.5 flex-wrap">
              <span>{total.toLocaleString()} eligible assets</span>
              <InfoButton {...HELP.screeningRules} testId="info-screening-rules" />
              <span>· {counts.evaluated.toLocaleString()} evaluated · {counts.pending.toLocaleString()} awaiting AI evaluation</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/animal-health">
              <Button variant="outline" size="sm" className="h-8 text-[12px]" data-testid="ah-cohort-back">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to screening
              </Button>
            </Link>
          </div>
        </header>

        {/* v27 — Recompute diff summary bar */}
        {cohortDiff && !cohortDiff.is_first_snapshot && (
          <div
            className="mb-3 bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]"
            data-testid="ah-cohort-diff-summary"
          >
            <span className="font-semibold text-slate-800">Last recompute:</span>
            <span className="text-slate-600">
              Previous <strong className="text-slate-900">{cohortDiff.previous_count}</strong>
              {' '}→ New <strong className="text-slate-900">{cohortDiff.new_count}</strong>
            </span>
            <span className="text-emerald-700">
              Newly added <strong>{cohortDiff.newly_added_count}</strong>
            </span>
            <span className="text-rose-700">
              Removed <strong>{cohortDiff.removed_count}</strong>
            </span>
            <span className="text-slate-600">
              Already evaluated <strong className="text-slate-900">{cohortDiff.already_evaluated}</strong>
            </span>
            <span className="text-indigo-700">
              AI pending <strong>{cohortDiff.ai_pending}</strong>
              {cohortDiff.ai_pending_new_only !== cohortDiff.ai_pending && (
                <span className="text-slate-500 font-normal"> ({cohortDiff.ai_pending_new_only} from newly added)</span>
              )}
            </span>
            {cohortDiff.previous_computed_at && (
              <span className="text-slate-400 ml-auto">
                Snapshot: {new Date(cohortDiff.previous_computed_at).toLocaleString()}
              </span>
            )}
          </div>
        )}

        {/* First-time snapshot notice — no previous cohort to diff against */}
        {cohortDiff && cohortDiff.is_first_snapshot && (
          <div
            className="mb-3 bg-slate-50 border border-slate-200 rounded-lg p-2 text-[11px] text-slate-600"
            data-testid="ah-cohort-diff-first"
          >
            First snapshot recorded ({cohortDiff.new_count} assets). Future recomputes will show what changed.
          </div>
        )}

        {/* Tier cards appear only once at least one asset has been evaluated */}
        {hasAny && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="ah-tier-cards">
            <button
              type="button"
              onClick={() => setTier('all')}
              className={`text-left border-2 rounded-lg p-4 transition-all hover:shadow-sm ${
                tier === 'all'
                  ? 'bg-slate-900 border-slate-900 text-white ring-2 ring-offset-1 ring-slate-500'
                  : 'bg-white border-slate-300 text-slate-900 hover:bg-slate-50'
              }`}
              data-testid="ah-tier-card-all"
            >
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-center gap-1">
                  <div className={`text-[13px] font-bold ${tier === 'all' ? 'text-white' : 'text-slate-900'}`}>All</div>
                  <span onClick={(e) => e.stopPropagation()}>
                    <InfoButton {...HELP.tierAll} testId="info-tier-all" />
                  </span>
                </div>
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${tier === 'all' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                  Full cohort
                </span>
              </div>
              <div className={`text-[32px] font-bold tabular-nums leading-tight ${tier === 'all' ? 'text-white' : 'text-slate-900'}`}>
                {counts.total}
                <span className={`text-[13px] font-medium ml-1.5 ${tier === 'all' ? 'text-white/70' : 'text-slate-500'}`}>
                  asset{counts.total === 1 ? '' : 's'}
                </span>
              </div>
              <p className={`text-[11px] mt-1 ${tier === 'all' ? 'text-white/80' : 'text-slate-500'}`}>
                Every eligible asset — evaluated or pending
              </p>
            </button>
            {['pursue', 'advance', 'pass'].map(id => {
              const meta = TIER_META[id];
              const n = counts[id] || 0;
              const selectedCard = tier === id;
              return (
                <button
                  type="button"
                  key={id}
                  onClick={() => setTier(selectedCard ? 'all' : id)}
                  className={`text-left border-2 rounded-lg p-4 transition-all hover:shadow-sm ${
                    selectedCard
                      ? `${meta.bg} ${meta.border} ring-2 ring-offset-1 ring-${id === 'pursue' ? 'emerald' : id === 'advance' ? 'amber' : 'slate'}-400`
                      : `bg-white ${meta.border} hover:${meta.bg}`
                  }`}
                  data-testid={`ah-tier-card-${id}`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-1">
                      <div className={`text-[13px] font-bold ${meta.text}`}>{meta.label}</div>
                      <span onClick={(e) => e.stopPropagation()}>
                        <InfoButton
                          {...(id === 'pursue' ? HELP.tierPursue : id === 'advance' ? HELP.tierAdvance : HELP.tierPass)}
                          testId={`info-tier-${id}`}
                        />
                      </span>
                    </div>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${meta.chipBg} ${meta.chipText}`}>
                      {meta.range}
                    </span>
                  </div>
                  <div className={`text-[32px] font-bold tabular-nums leading-tight ${meta.text}`}>
                    {n}
                    <span className="text-[13px] font-medium text-slate-500 ml-1.5">
                      asset{n === 1 ? '' : 's'} · {pctOf(n)}%
                    </span>
                  </div>
                  <p className={`text-[11px] mt-1 ${meta.text} opacity-80`}>{meta.subtitle}</p>
                </button>
              );
            })}
          </div>
        )}

        {/* Action bar */}
        <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4 flex flex-wrap items-center gap-2">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search asset / company / TA…"
            className="h-8 text-[12px] w-[280px]"
            data-testid="ah-cohort-search"
          />
          <div className="flex items-center gap-1 flex-wrap">
            {[
              { id: 'all',         label: `All (${counts.total})` },
              ...(hasAny ? [
                { id: 'pursue',      label: `Pursue Now (${counts.pursue})` },
                { id: 'advance',     label: `Advance / Monitor (${counts.advance})` },
                { id: 'pass',        label: `Park / Pass (${counts.pass})` },
              ] : []),
              ...(counts.running > 0 ? [{ id: 'running', label: `Running (${counts.running})` }] : []),
              ...(counts.failed > 0 ? [{ id: 'failed', label: `Failed (${counts.failed})` }] : []),
              { id: 'unevaluated', label: `Pending (${counts.pending})` },
            ].map(f => {
              const active = tier === f.id;
              const meta = TIER_META[f.id];
              return (
                <button
                  type="button"
                  key={f.id}
                  onClick={() => setTier(f.id)}
                  className={`px-2.5 py-1 rounded text-[11px] font-medium ${
                    active
                      ? (meta ? `${meta.activeBg} ${meta.activeText}` : 'bg-indigo-600 text-white')
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid={`ah-cohort-filter-${f.id}`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {selected.size > 0 && !isClientPortal && (
              <>
                <span className="text-[11px] text-slate-600">{selected.size} selected</span>
                <Button
                  size="sm"
                  onClick={() => runCohortEval('selected')}
                  disabled={running || evalTask?.status === 'running'}
                  className="h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="ah-cohort-run-selected"
                >
                  <Sparkles className="w-3 h-3 mr-1" /> Run AI on selected
                </Button>
                <Button size="sm" variant="ghost" onClick={clearSel} className="h-8 text-[11px]">Clear</Button>
              </>
            )}
            {!isClientPortal && (
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={recomputeCohort}
                disabled={loading || recomputing || running || evalTask?.status === 'running'}
                className="h-8 text-[11px]"
                data-testid="ah-cohort-recompute"
                title="Re-run screening rules and diff against the last snapshot"
              >
                {recomputing
                  ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Recomputing…</>
                  : <><RefreshCw className="w-3 h-3 mr-1" />Recompute</>}
              </Button>
            </div>
            )}
            {!isClientPortal && (
            <div className="flex items-center gap-1">
              {newPending > 0 ? (
                <>
                  <Button
                    size="sm"
                    onClick={() => runCohortEval('new')}
                    disabled={running || evalTask?.status === 'running'}
                    className="h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="ah-cohort-run-new"
                    title="Score only the newly-added assets since the last recompute"
                  >
                    {running || evalTask?.status === 'running'
                      ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Running…</>
                      : <><Sparkles className="w-3 h-3 mr-1" />Run AI on {newPending} Newly Added</>}
                  </Button>
                  {counts.pending > newPending && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runCohortEval('all')}
                      disabled={running || evalTask?.status === 'running'}
                      className="h-8 text-[11px]"
                      data-testid="ah-cohort-run-all-pending"
                      title="Score every pending asset in the cohort (includes assets pending from previous runs)"
                    >
                      All pending ({counts.pending})
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  size="sm"
                  onClick={() => runCohortEval('all')}
                  disabled={running || evalTask?.status === 'running' || counts.pending === 0}
                  className="h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="ah-cohort-run-all"
                  title={counts.pending === 0 ? 'All eligible assets are already scored' : 'Score every pending asset'}
                >
                  {running || evalTask?.status === 'running'
                    ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Running…</>
                    : <><Sparkles className="w-3 h-3 mr-1" />Run AI on Cohort ({counts.pending})</>}
                </Button>
              )}
              <InfoButton {...HELP.runAiCohort} testId="info-run-ai-cohort" />
            </div>
            )}
            {!isClientPortal && (
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={exportCsv}
                disabled={sorted.length === 0}
                className="h-8 text-[11px]"
                data-testid="ah-cohort-export"
              >
                <Download className="w-3 h-3 mr-1" /> Export CSV
              </Button>
              <InfoButton {...HELP.exportCsv} testId="info-export-csv" />
            </div>
            )}
          </div>
        </div>

        {/* Live evaluation progress panel (v27) */}
        {evalTask && evalTask.status === 'running' && (
          <div className="mb-3 bg-white border border-emerald-200 rounded-lg p-3 shadow-sm" data-testid="ah-cohort-eval-progress">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                <span className="text-[13px] font-semibold text-emerald-900">Evaluating Animal Health Opportunities</span>
              </div>
              <div className="text-[11px] text-slate-500">
                Claude Sonnet 4.5 · 3 concurrent · results update in-place
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
              {[
                { label: 'Total', value: evalTask.total ?? '—', cls: 'text-slate-700' },
                { label: 'Pending', value: evalTask.pending ?? '—', cls: 'text-slate-600' },
                { label: 'Running', value: evalTask.running ?? 0, cls: 'text-indigo-700' },
                { label: 'Completed', value: evalTask.assessed ?? 0, cls: 'text-emerald-700' },
                { label: 'Failed', value: evalTask.errors ?? 0, cls: (evalTask.errors ? 'text-rose-700' : 'text-slate-500') },
              ].map(t => (
                <div key={t.label} className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                  <div className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold">{t.label}</div>
                  <div className={`text-[16px] font-bold tabular-nums leading-none ${t.cls}`}>{t.value}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 mb-1">
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${evalTask.percent ?? 0}%` }}
                />
              </div>
              <span className="text-[12px] font-semibold text-emerald-900 tabular-nums w-14 text-right">
                {evalTask.percent ?? 0}%
              </span>
            </div>
            <div className="text-[11px] text-slate-600">
              Estimated remaining: <span className="font-semibold">{formatEta(evalTask.eta_seconds)}</span>
            </div>
          </div>
        )}

        {/* Cohort table — virtual scrolling (v27, replaces pagination) */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 flex items-center gap-2 text-slate-500 text-[13px]">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading eligible cohort…
            </div>
          ) : sorted.length === 0 ? (
            <div className="p-10 text-center text-[13px] text-slate-600" data-testid="ah-cohort-empty-state">
              {loadError ? (
                <>
                  <div className="text-[15px] text-rose-700 font-semibold mb-1">
                    Couldn&apos;t load the cohort
                  </div>
                  <div className="text-rose-600 mb-3">{loadError}</div>
                  <div className="text-slate-500 mb-3">
                    Most common cause: the screening rules are empty, or the deep-dive payload
                    exceeded the proxy limit. Open the Screening Rules page for a one-click fix.
                  </div>
                  <Link
                    to="/animal-health"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-indigo-600 text-white text-[12px] font-medium hover:bg-indigo-700"
                    data-testid="ah-cohort-goto-rules"
                  >
                    <ArrowLeft className="w-3 h-3" /> Open Screening Rules
                  </Link>
                </>
              ) : (
                <>No assets in this view. Try a different filter or adjust the screening rules.</>
              )}
            </div>
          ) : (
            <div data-testid="ah-cohort-table">
              {/* Sticky header — grid mirrors the row template below */}
              <div
                className="grid bg-slate-50 border-b border-slate-200 sticky top-0 z-10"
                style={{ gridTemplateColumns: TABLE_GRID_COLUMNS }}
              >
                <div className="px-2 py-2 flex items-center">
                  <input
                    type="checkbox"
                    onChange={toggleAllVisible}
                    checked={
                      sorted.filter(a => !a.assessment).length > 0
                        && sorted.filter(a => !a.assessment).every(a => selected.has(a.id))
                    }
                    data-testid="ah-cohort-select-all"
                    aria-label="Select all pending assets"
                  />
                </div>
                <GridSortHeader label="Asset" field="asset_name" sort={sort} setSort={setSort} />
                <GridSortHeader label="Company" field="company_name" sort={sort} setSort={setSort} />
                <GridSortHeader label="Phase" field="phase" sort={sort} setSort={setSort} />
                <GridSortHeader label="Therapeutic Area" field="therapeutic_area" sort={sort} setSort={setSort} />
                <GridHeader label="AI Status" help={HELP.aiStatus} />
                <GridSortHeader label="AI Score" field="ai_opportunity_score" sort={sort} setSort={setSort} help={HELP.aiScore} />
                <GridHeader label="Decision" help={HELP.decision} />
                <GridHeader label="Animal Data" />
                <GridHeader label="Actions" help={HELP.actions} />
              </div>
              {/* Virtual scroll container */}
              <div
                ref={scrollRef}
                className="overflow-y-auto"
                style={{ height: 'calc(100vh - 380px)', minHeight: '400px' }}
                data-testid="ah-cohort-scroll"
              >
                <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
                  {virtualRows.map(vRow => {
                    const a = sorted[vRow.index];
                    if (!a) return null;
                    const isRunning = a.ai_status === 'running';
                    const isFailed = a.ai_status === 'failed';
                    const done = a.ai_status === 'completed';
                    return (
                      <div
                        key={a.id}
                        className={`grid absolute top-0 left-0 w-full border-b border-slate-100 hover:bg-slate-50 items-center text-[12px] ${selected.has(a.id) ? 'bg-indigo-50/40' : ''}`}
                        style={{
                          gridTemplateColumns: TABLE_GRID_COLUMNS,
                          height: `${vRow.size}px`,
                          transform: `translateY(${vRow.start}px)`,
                        }}
                        data-testid={`ah-cohort-row-${a.id}`}
                      >
                        <div className="px-2 flex items-center">
                          <input
                            type="checkbox"
                            checked={selected.has(a.id)}
                            onChange={() => toggleOne(a.id)}
                            disabled={done}
                          />
                        </div>
                        <div className="px-3 font-medium text-slate-900 truncate" title={a.asset_name || ''}>
                          <a href={`/assets/${a.id}`} target="_blank" rel="noopener noreferrer" className="text-indigo-700 hover:underline">
                            {a.asset_name || '—'}
                          </a>
                        </div>
                        <div className="px-3 text-slate-700 truncate" title={a.company_name || ''}>{a.company_name || '—'}</div>
                        <div className="px-3 text-slate-700 truncate">{a.phase || '—'}</div>
                        <div className="px-3 text-slate-700 truncate" title={a.therapeutic_area || ''}>{a.therapeutic_area || '—'}</div>
                        <div className="px-3"><AIStatusBadge status={a.ai_status} /></div>
                        <div className="px-3">
                          {done ? (
                            <ScoreBar score={a.ai_opportunity_score} tierId={a.tier_id} />
                          ) : (
                            <span className="text-slate-300 tabular-nums">—</span>
                          )}
                        </div>
                        <div className="px-3">
                          {done ? <TierChip recommendation={a.recommendation} size="sm" short /> : <span className="text-slate-300">—</span>}
                        </div>
                        <div className="px-3">
                          <AnimalDataPill status={animalStatus[a.id] || 'not_reviewed'} />
                        </div>
                        <div className="px-3">
                          {done ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDeepDive(a.assessment)}
                              className="h-7 text-[11px]"
                              data-testid={`ah-cohort-deep-dive-${a.id}`}
                            >
                              <ScanEye className="w-3 h-3 mr-1" /> Deep Dive
                            </Button>
                          ) : isRunning ? (
                            <Button size="sm" variant="outline" disabled className="h-7 text-[11px]" data-testid={`ah-cohort-running-${a.id}`}>
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Running…
                            </Button>
                          ) : isClientPortal ? (
                            <span className="text-[11px] text-slate-400">—</span>
                          ) : isFailed ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => runSingle(a.id)}
                              disabled={running}
                              className="h-7 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                              data-testid={`ah-cohort-retry-${a.id}`}
                              title="Previous attempt failed — click to retry"
                            >
                              <RotateCcw className="w-3 h-3 mr-1" /> Retry
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => runSingle(a.id)}
                              disabled={running || evalTask?.status === 'running'}
                              className="h-7 text-[11px]"
                              data-testid={`ah-cohort-runai-${a.id}`}
                            >
                              <Sparkles className="w-3 h-3 mr-1" /> Run AI
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Continuous-scroll indicator (replaces pagination footer) */}
              <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-slate-50 text-[11px]">
                <div className="text-slate-600 flex items-center gap-2">
                  <span>
                    {sorted.length.toLocaleString()} {sorted.length === 1 ? 'asset' : 'assets'} in view
                    {sorted.length !== counts.total ? ` · ${counts.total.toLocaleString()} in full cohort` : ''}
                  </span>
                  {sorted.length !== counts.total && (
                    <button
                      type="button"
                      onClick={() => { setTier('all'); setSearch(''); }}
                      className="text-indigo-700 hover:text-indigo-900 hover:underline font-medium"
                      data-testid="ah-cohort-show-all"
                    >
                      Show all {counts.total.toLocaleString()} →
                    </button>
                  )}
                </div>
                <div className="text-slate-500">
                  Scroll to see more — no pagination
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DeepDiveModal
        open={!!ddItem}
        onClose={() => setDdItem(null)}
        item={ddItem}
        criteria={criteria}
        onManualDecisionSaved={onManualDecisionSaved}
      />
    </div>
  );
}

// Grid template for the virtualised table (must match header + rows).
// 10 columns: checkbox | Asset | Company | Phase | TA | AI Status | AI Score | Decision | Animal Data | Actions
const TABLE_GRID_COLUMNS =
  '28px minmax(170px, 1.7fr) minmax(140px, 1.3fr) 90px minmax(140px, 1.2fr) 110px 160px 150px 140px 120px';

const GridHeader = ({ label, help }) => (
  <div className="px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wide text-slate-600">
    <span className="inline-flex items-center gap-1">
      {label}
      {help && <InfoButton {...help} testId={`info-col-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />}
    </span>
  </div>
);

const GridSortHeader = ({ label, field, sort, setSort, help }) => {
  const active = sort.field === field;
  const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <div className="px-3 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wide text-slate-600 select-none">
      <span className="inline-flex items-center gap-1">
        <span
          className="inline-flex items-center gap-1 cursor-pointer hover:text-slate-900"
          onClick={() => setSort(s => ({ field, dir: s.field === field && s.dir === 'desc' ? 'asc' : 'desc' }))}
        >
          {label}
          <Icon className={`w-3 h-3 ${active ? 'text-indigo-600' : 'text-slate-400'}`} />
        </span>
        {help && <InfoButton {...help} testId={`info-col-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />}
      </span>
    </div>
  );
};
